// Identification field-map + small display helpers.
//
// The v2 mock renders ~24 AFE-identification fields. Five of them are backed by
// an afe_headers column (returned in the BE `fields` block); the rest live in
// the open-ended `extension_data` jsonb (returned in `extension_data`, merged
// opaquely by `Document::Afe#update_identification!`). `IDENTIFICATION_FIELDS` is the single
// source of truth that drives BOTH the read-only Field/Value table and the edit
// SidePanel, so the label set / order / grouping never drift between them.
//
// Wire contract (KEEP — production-correct):
//   * responses are camelCase (jbuilder key_format) — column values read from
//     `fields[responseKey]`; extension values read from `extensionData[key]`.
//   * request bodies are snake_case — column fields submit flat under
//     `identification` (afe_number, afe_type, operator, afe_date, held_flag);
//     extension fields submit under `identification.extension_data` keyed by
//     the SAME snake_case `key` used to read them back.

import type {
  IdentificationExtensionData,
  IdentificationFields,
} from './types-identification';

/** Which side of the BE payload a field lives on. */
export type FieldGroup = 'column' | 'extension';

/** Rendered control type in the edit panel (view always renders as text). */
export type FieldInputType = 'text' | 'date' | 'dropdown';

export interface IdentificationFieldDef {
  /** Human label shown in the Field column + the edit-panel input label. */
  label: string;
  /**
   * For column fields: the camelCase key inside the BE `fields` block AND the
   * snake_case request key are derived from `columnKey`.
   * For extension fields: the snake_case key inside `extension_data` (used for
   * BOTH read and write — extension responses are camelCase only at the top
   * level, the jsonb sub-keys round-trip verbatim).
   */
  key: string;
  group: FieldGroup;
  input: FieldInputType;
  /**
   * Dropdown choices (only for input === 'dropdown'). A plain string is used as
   * both the label and the stored value; a {label, value} pair decouples the
   * two (e.g. the Hold Flag enum shows "Held" but stores "held").
   */
  options?: ReadonlyArray<string | { label: string; value: string }>;
  /** Layout column in the edit panel (mirrors v2's two-column grid). */
  panelColumn: 1 | 2;
}

// Dropdown option sets ported verbatim from the v2 mock.
export const DEADLINE_TYPE_OPTIONS = ['Soft', 'Hard'];
export const YES_NO_OPTIONS = ['Yes', 'No'];

// Hold Flag is an afe_headers enum (held / submittal; nil = none). The dropdown
// shows friendly labels ("Held" / "Submittal") but stores the raw enum value, so
// the BE save (which validates against the enum) stays valid. Shared by the Cost
// section's editable Hold Flag dropdown + display label.
export const HOLD_FLAG_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Held', value: 'held' },
  { label: 'Submittal', value: 'submittal' },
];

// Standardized AFE Type vocabulary (extension free-text), ported from the v2
// mock. An existing out-of-list value is preserved by the edit panel rather
// than silently cleared.
export const STANDARDIZED_AFE_TYPE_OPTIONS = [
  'Drilling, Completion, & Equipping (DC&E)',
  'Plug & Abandonment (P&A)',
  'Workover',
  'Facility',
  'Recompletion',
  'Other',
];

// ---------------------------------------------------------------------------
// The 24-field map. Order matches the v2 read-only table (Layout2 ~4424-4480);
// `panelColumn` matches the v2 edit SidePanel's two-column split
// (renderAfeFormFields, Layout2 ~3349-3613).
//
// Column fields (group: 'column') are a subset of
// Document::Afe::IDENTIFICATION_FIELDS (afe_number, afe_type, operator, afe_date, held_flag): the FE
// renders afeNumber / afeType / afeDate here. Operator, Operator Code, and Held
// Flag were intentionally dropped from this section — Held Flag now lives in the
// Cost section. The BE columns still exist; they're just not surfaced here.
// Everything else is extension_data (snake_case key == read key).
// ---------------------------------------------------------------------------
export const IDENTIFICATION_FIELDS: IdentificationFieldDef[] = [
  // Row order matches the v2 mock read-only table (the column-backed fields —
  // afeNumber/afeType/afeDate — are interleaved with the extension fields, NOT
  // grouped first). `group` still drives request serialization; only the
  // display order/labels are aligned to the mock.
  {
    label: 'AFE Number',
    key: 'afeNumber',
    group: 'column',
    input: 'text',
    panelColumn: 1,
  },
  {
    label: 'AFE Name',
    key: 'afe_name',
    group: 'extension',
    input: 'text',
    panelColumn: 1,
  },
  {
    label: 'AFE Type',
    key: 'afeType',
    group: 'column',
    input: 'text',
    panelColumn: 1,
  },
  {
    label: 'Standardized AFE Type',
    key: 'standardized_afe_type',
    group: 'extension',
    input: 'dropdown',
    options: STANDARDIZED_AFE_TYPE_OPTIONS,
    panelColumn: 1,
  },
  {
    // Extraction stores the cost center under `cost_center_number` (the citation
    // provision_field), and extension_data keys round-trip verbatim, so the read
    // AND write key must be `cost_center_number` to match the provisioned value.
    label: 'Cost Center',
    key: 'cost_center_number',
    group: 'extension',
    input: 'text',
    panelColumn: 1,
  },
  {
    label: 'AFE Dated Date',
    key: 'afeDate',
    group: 'column',
    input: 'date',
    panelColumn: 1,
  },
  {
    label: 'AFE Received Date',
    key: 'received_date',
    group: 'extension',
    input: 'date',
    panelColumn: 1,
  },
  {
    // Mock renders AFE Executed Date as a free-text input (its seed value is the
    // prose "Not yet executed"), so this stays 'text' rather than a date-picker
    // to allow non-date values. KEEP in sync with the v2 mock.
    label: 'AFE Executed Date',
    key: 'executed_date',
    group: 'extension',
    input: 'text',
    panelColumn: 1,
  },
  {
    label: 'Deadline Date',
    key: 'deadline_date',
    group: 'extension',
    input: 'date',
    panelColumn: 1,
  },
  {
    label: 'Deadline Type',
    key: 'deadline_type',
    group: 'extension',
    input: 'dropdown',
    options: DEADLINE_TYPE_OPTIONS,
    panelColumn: 2,
  },
  {
    label: 'Term',
    key: 'term',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
  {
    label: 'Est. Work Date',
    key: 'est_work_date',
    group: 'extension',
    input: 'date',
    panelColumn: 2,
  },
  {
    label: 'Est. JIB Date',
    key: 'est_jib_date',
    group: 'extension',
    input: 'date',
    panelColumn: 2,
  },
  {
    label: 'Non Consent Penalty',
    key: 'non_consent_penalty',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
  {
    label: 'Subject to Existing JOA',
    key: 'subject_to_existing_joa',
    group: 'extension',
    input: 'dropdown',
    options: YES_NO_OPTIONS,
    panelColumn: 2,
  },
  {
    label: 'Subject to Pooling Order',
    key: 'subject_to_pooling_order',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
  {
    label: 'Cash Call',
    key: 'cash_call',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
  {
    label: 'Cash Call Due Date',
    key: 'cash_call_due_date',
    group: 'extension',
    input: 'date',
    panelColumn: 2,
  },
  {
    label: '% Paid',
    key: 'percent_paid',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
  {
    label: 'Revision',
    key: 'revision',
    group: 'extension',
    input: 'dropdown',
    options: YES_NO_OPTIONS,
    panelColumn: 2,
  },
  {
    label: 'Explanation & Justification',
    key: 'explanation_and_justification',
    group: 'extension',
    input: 'text',
    panelColumn: 2,
  },
];

const EM_DASH = '—';

/**
 * Render an extension-data value for the read-only grid.
 * @param {string | number | boolean | null | undefined} value - The raw extension value.
 * @return {string} The display string (booleans → Yes/No, blank → em-dash).
 */
export const displayExtensionValue = (
  value: string | number | boolean | null | undefined
): string => {
  if (value === null || value === undefined || value === '') return EM_DASH;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/**
 * Read a single primitive value off the column `fields` / `extension_data`.
 * @param {IdentificationFieldDef} def - The field definition.
 * @param {IdentificationFields} fields - The column-backed `fields` block.
 * @param {IdentificationExtensionData} extensionData - The `extension_data` block.
 * @return {string | number | boolean | null} The raw value, or null when absent.
 */
export const fieldRawValue = (
  def: IdentificationFieldDef,
  fields: IdentificationFields,
  extensionData: IdentificationExtensionData
): string | number | boolean | null => {
  if (def.group === 'column') {
    return (
      (fields as unknown as Record<string, string | null>)[def.key] ?? null
    );
  }
  return extensionData[def.key] ?? null;
};

/**
 * Resolve a stored value to its dropdown label (e.g. "held" → "Held").
 * @param {IdentificationFieldDef} def - The field definition.
 * @param {string | number | boolean | null} raw - The stored value.
 * @return {string | null} The matching dropdown label, or null when none.
 */
const optionLabel = (
  def: IdentificationFieldDef,
  raw: string | number | boolean | null
): string | null => {
  if (!def.options || raw === null || raw === undefined || raw === '') {
    return null;
  }
  const match = def.options.find(
    (o) => typeof o !== 'string' && o.value === String(raw)
  );
  return match && typeof match !== 'string' ? match.label : null;
};

/**
 * Display string for the Field/Value table (booleans → Yes/No, blank → dash).
 * @param {IdentificationFieldDef} def - The field definition.
 * @param {IdentificationFields} fields - The column-backed `fields` block.
 * @param {IdentificationExtensionData} extensionData - The `extension_data` block.
 * @return {string} The display string for the read-only table.
 */
export const fieldDisplayValue = (
  def: IdentificationFieldDef,
  fields: IdentificationFields,
  extensionData: IdentificationExtensionData
): string => {
  const raw = fieldRawValue(def, fields, extensionData);
  return optionLabel(def, raw) ?? displayExtensionValue(raw);
};

/**
 * String form for an edit-panel input ('' instead of dash for empties).
 * @param {IdentificationFieldDef} def - The field definition.
 * @param {IdentificationFields} fields - The column-backed `fields` block.
 * @param {IdentificationExtensionData} extensionData - The `extension_data` block.
 * @return {string} The input value string ('' for empties).
 */
export const fieldInputValue = (
  def: IdentificationFieldDef,
  fields: IdentificationFields,
  extensionData: IdentificationExtensionData
): string => {
  const raw = fieldRawValue(def, fields, extensionData);
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  return String(raw);
};

export const COLUMN_FIELDS = IDENTIFICATION_FIELDS.filter(
  (f) => f.group === 'column'
);
export const EXTENSION_FIELDS = IDENTIFICATION_FIELDS.filter(
  (f) => f.group === 'extension'
);
